import { HttpClient } from '@angular/common/http';
import { Inject, Injectable, Optional } from '@angular/core';
import { map, Observable, of, switchMap } from 'rxjs';
import { filter, groupBy, map as _map } from 'lodash';

import {
  Client,
  ClientCreate,
  ClientListEntity,
  ClientUpdate,
} from '@lockton-apps/shared/interfaces';
import { getDevConfig } from '@lockton-apps/shared/util/helpers';
import { MocksService } from '@lockton-apps/shared/util/mocks';

@Injectable({
  providedIn: 'root',
})
export class ClientsService {
  modelPlural = 'clients';
  modelSingular = 'client';
  modelCombi = 'client/clients';

  constructor(
    @Optional() private readonly mocksService: MocksService,
    @Inject('BASE_API_URL') private baseUrl: string,
    @Inject('BASE_API_URL_V2') private baseUrlv2: string,
    private http: HttpClient
  ) {}

  private _getUrl() {
    return `${this.baseUrl}/${this.modelSingular}`;
  }

  private _getAllUrl() {
    return `${this.baseUrl}/${this.modelPlural}/dashboard`;
  }

  private _getCombiUrl(id: number) {
    return `${this.baseUrl}/${this.modelCombi}/${id}`;
  }

  private _getUrlForId(id: number) {
    return `${this._getUrl()}/${id}`;
  }

  private _getUrlForIsLead(id: number) {
    return `${this._getUrl()}/userisleadcontact/${id}`;
  }

  private _getUrlForSingleClientId(singleCLientId: string) {
    return `${this._getUrl()}/singleId/${singleCLientId}`;
  }

  private _getUrlForLocktonId(locktonId: string) {
    return `${this._getUrl()}/locktonId/${locktonId}`;
  }

  private _getUrlForContactClients() {
    return `${this.baseUrl}/${this.modelPlural}`;
  }

  all() {
    return this.http.get<Client[]>(this._getAllUrl());
  }

  allListEntities(): Observable<ClientListEntity[]> {
    return this.http
      .get<Omit<ClientListEntity, 'subsidiaryCount'>[]>(
        `${this.baseUrlv2}/clients/dashboard`
      )
      .pipe(
        map((clients) => {
          const subsidiariesMap = groupBy(
            filter(clients, (x) => !x.isPrimaryClient),
            (x) => x.relatesToClientId
          );

          return _map(clients, (client) => ({
            ...client,
            subsidiaryCount: subsidiariesMap[client.id]?.length || 0,
          }));
        })
      );
  }

  allForContact() {
    return this.http.get<Client[]>(this._getUrlForContactClients());
  }

  load(id: number) {
    if (getDevConfig('client.fullMock')) {
      return of(this.mocksService.getClient(id));
    }

    return this.http.get<Client>(this._getUrlForId(id));
  }

  loadClientSubClients(id: number) {
    return this.http.get<Client[]>(this._getCombiUrl(id));
  }

  loadClientAndSubClients(id: number) {
    return this.load(id).pipe(
      switchMap((client) => {
        if (client.isPrimaryClient) {
          return this.loadClientSubClients(id).pipe(
            map((clients) => [client, ...clients])
          );
        }
        return of([client]);
      })
    );
  }

  loadAllRelated(id: number) {
    return this.load(id).pipe(
      switchMap((client) => {
        if (client.isPrimaryClient) {
          return this.loadClientSubClients(id).pipe(
            map((clients) => [clients, ...clients])
          );
        }
        if (client.relatesToClientId) {
          return this.loadClientAndSubClients(client.relatesToClientId);
        }
        return of([client]);
      })
    );
  }

  create(client: ClientCreate) {
    return this.http.post<Client>(this._getUrl(), client);
  }

  update(client: ClientUpdate) {
    return this.http.patch<Client>(this._getUrl(), client);
  }

  delete(client: Client) {
    return this.http.delete(this._getUrlForId(client.id));
  }

  isLeadContact(id: number) {
    return this.http.get<{ isPrimaryContact: boolean }>(
      this._getUrlForIsLead(id)
    );
  }

  checkSingleClientIdExists(singleCLientId: string) {
    return this.http.get<boolean>(
      this._getUrlForSingleClientId(singleCLientId)
    );
  }

  checkLocktonIdExists(locktonId: string) {
    return this.http.get<boolean>(this._getUrlForLocktonId(locktonId));
  }

  getAutoGeneratedEmailPreferences(clientId: number): Observable<boolean> {
    return this.http.get<boolean>(
      `${this.baseUrl}/email/client/${clientId}/suppressed`
    );
  }

  toggleAutoGeneratedEmailPreferences(clientId: number, value: boolean) {
    if (!value) {
      return this.http.post(
        `${this.baseUrl}/email/client/suppression`,
        {
          clientId,
          templateId: '*',
        },
        { responseType: 'text' }
      );
    }

    return this.http.delete(
      `${this.baseUrl}/email/client/${clientId}/suppression/*`,
      { responseType: 'text' }
    );
  }
}
